import { ChangeDetectorRef, Component, Input, NgZone, OnChanges, OnDestroy } from '@angular/core';

/**
 * Countdown to DVLA putting a pre-release series on sale (27: 10:00 UK,
 * Wed 4 Nov 2026). Ticks once a second outside Angular's zone so the rest
 * of the page isn't re-checked every second; flips to an "on sale" line
 * once the time has passed.
 */
@Component({
  selector: 'pla-new-release-countdown',
  templateUrl: './new-release-countdown.component.html',
  styleUrls: ['./new-release-countdown.component.scss'],
})
export class NewReleaseCountdownComponent implements OnChanges, OnDestroy {
  /** ISO instant the sale opens, e.g. '2026-11-04T10:00:00Z'. */
  @Input() opensAt = '';
  @Input() year = '';
  /** 'sale': the dark band under the hero. 'list': compact clock inside the
   *  members' card, counting down to the list unlock. */
  @Input() mode: 'sale' | 'list' = 'sale';

  days = 0;
  hours = 0;
  minutes = 0;
  seconds = 0;
  open = false;
  label = '';

  private timer?: ReturnType<typeof setInterval>;

  constructor(private zone: NgZone, private cdr: ChangeDetectorRef) {}

  ngOnChanges(): void {
    this.label = this.formatLabel(new Date(this.opensAt));
    this.tick();
    this.stop();
    if (!this.open) {
      this.zone.runOutsideAngular(() => {
        this.timer = setInterval(() => { this.tick(); this.cdr.detectChanges(); }, 1000);
      });
    }
  }

  ngOnDestroy(): void { this.stop(); }

  /** "10:00, Wednesday 4 November" in UK time, whatever the reader's zone. */
  private formatLabel(at: Date): string {
    if (isNaN(at.getTime())) return '';
    const parts: Record<string, string> = {};
    new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/London', weekday: 'long', day: 'numeric', month: 'long',
      hour: '2-digit', minute: '2-digit', hour12: false,
    }).formatToParts(at).forEach((p) => { parts[p.type] = p.value; });
    return `${parts['hour']}:${parts['minute']}, ${parts['weekday']} ${parts['day']} ${parts['month']}`;
  }

  pad(n: number): string { return n < 10 ? '0' + n : String(n); }

  private tick(): void {
    const left = new Date(this.opensAt).getTime() - Date.now();
    if (!(left > 0)) {
      this.open = true;
      this.days = this.hours = this.minutes = this.seconds = 0;
      this.stop();
      return;
    }
    this.open = false;
    const s = Math.floor(left / 1000);
    this.days = Math.floor(s / 86400);
    this.hours = Math.floor((s % 86400) / 3600);
    this.minutes = Math.floor((s % 3600) / 60);
    this.seconds = s % 60;
  }

  private stop(): void {
    if (this.timer) { clearInterval(this.timer); this.timer = undefined; }
  }
}
